"use client"

import { useMemo, useState } from "react"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import Drilldown from "highcharts/modules/drilldown"

// init once
if (typeof Highcharts === "object") {
  Drilldown(Highcharts)
}

const toDateOnly = (v) => {
  if (!v) return ""
  if (typeof v === "string") return v.split("T")[0]
  try {
    return new Date(v).toISOString().split("T")[0]
  } catch (e) {
    return String(v)
  }
}

const monthStart = (ym) => ym + "-01"

const monthEnd = (ym) => {
  const y = Number(ym.slice(0, 4))
  const m = Number(ym.slice(5, 7)) // 1..12
  const last = new Date(y, m, 0).getDate()
  return ym + "-" + String(last).padStart(2, "0")
}

const HistoricChartDrilldown = ({
  sales_trend_month_wise = [],
  sales_trend_datewise = [],
}) => {
  const [viewMode, setViewMode] = useState("month") // "month" | "day"
  const [dayRange, setDayRange] = useState(null) // { from:"YYYY-MM-DD", to:"YYYY-MM-DD" } | null
  const [clickedMonth, setClickedMonth] = useState(null) // Track clicked month for drilldown

  const stores = useMemo(() => {
    const set = new Set()

    for (let i = 0; i < sales_trend_month_wise.length; i++) {
      const r = sales_trend_month_wise[i]
      const s = r && r.destination_store_name
      if (s) set.add(s)
    }

    if (set.size === 0) {
      for (let i = 0; i < sales_trend_datewise.length; i++) {
        const r = sales_trend_datewise[i]
        const s = r && r.destination_store_name
        if (s) set.add(s)
      }
    }

    return Array.from(set)
  }, [sales_trend_month_wise, sales_trend_datewise])

  const months = useMemo(() => {
    const set = new Set()
    for (let i = 0; i < sales_trend_month_wise.length; i++) {
      const r = sales_trend_month_wise[i]
      const m = r && r.month_period
      if (m) set.add(m)
    }
    return Array.from(set).sort()
  }, [sales_trend_month_wise])

  // monthQty[store][month] = qty
  const monthQty = useMemo(() => {
    const out = {}
    for (let i = 0; i < stores.length; i++) out[stores[i]] = {}

    for (let i = 0; i < sales_trend_month_wise.length; i++) {
      const r = sales_trend_month_wise[i]
      if (!r) continue

      const store = r.destination_store_name
      const month = r.month_period
      if (!store || !month) continue

      if (!out[store]) out[store] = {}
      out[store][month] = (out[store][month] || 0) + (Number(r.sale_quantity) || 0)
    }

    return out
  }, [sales_trend_month_wise, stores])

  // dayQtyByMonth[store][YYYY-MM][YYYY-MM-DD] = qty (for drilldown click)
  const dayQtyByMonth = useMemo(() => {
    const out = {}
    for (let i = 0; i < stores.length; i++) out[stores[i]] = {}

    for (let i = 0; i < sales_trend_datewise.length; i++) {
      const r = sales_trend_datewise[i]
      if (!r) continue

      const store = r.destination_store_name
      const date = toDateOnly(r.invoice_date)
      if (!store || !date) continue

      const month = date.slice(0, 7)

      if (!out[store]) out[store] = {}
      if (!out[store][month]) out[store][month] = {}

      out[store][month][date] =
        (out[store][month][date] || 0) + (Number(r.sale_quantity) || 0)
    }

    return out
  }, [sales_trend_datewise, stores])

  // allDates for day-wise all destinations
  const allDates = useMemo(() => {
    const set = new Set()
    for (let i = 0; i < sales_trend_datewise.length; i++) {
      const r = sales_trend_datewise[i]
      const d = r && toDateOnly(r.invoice_date)
      if (d) set.add(d)
    }
    return Array.from(set).sort()
  }, [sales_trend_datewise])

  // dayAllQty[store][date] = qty
  const dayAllQty = useMemo(() => {
    const out = {}
    for (let i = 0; i < stores.length; i++) out[stores[i]] = {}

    for (let i = 0; i < sales_trend_datewise.length; i++) {
      const r = sales_trend_datewise[i]
      if (!r) continue

      const store = r.destination_store_name
      const date = toDateOnly(r.invoice_date)
      if (!store || !date) continue

      if (!out[store]) out[store] = {}
      out[store][date] = (out[store][date] || 0) + (Number(r.sale_quantity) || 0)
    }

    return out
  }, [sales_trend_datewise, stores])

  // filter day-wise dates based on zoom-selected month range
  const filteredDates = useMemo(() => {
    if (!dayRange) return allDates
    return allDates.filter((d) => d >= dayRange.from && d <= dayRange.to)
  }, [allDates, dayRange])

  // Get all dates for a specific month
  const getDatesForMonth = (month) => {
    const monthDates = new Set()
    for (let i = 0; i < sales_trend_datewise.length; i++) {
      const r = sales_trend_datewise[i]
      const date = toDateOnly(r.invoice_date)
      if (date && date.startsWith(month)) {
        monthDates.add(date)
      }
    }
    return Array.from(monthDates).sort()
  }

  // Get all dates for a month range
  const getDatesForMonthRange = (fromMonth, toMonth) => {
    const allDatesInRange = []
    
    // Generate all months in range
    const monthsInRange = []
    let currentMonth = fromMonth
    
    while (currentMonth <= toMonth) {
      monthsInRange.push(currentMonth)
      
      // Increment month
      const [year, month] = currentMonth.split('-').map(Number)
      let nextYear = year
      let nextMonth = month + 1
      
      if (nextMonth > 12) {
        nextMonth = 1
        nextYear = year + 1
      }
      
      currentMonth = `${nextYear}-${String(nextMonth).padStart(2, '0')}`
    }
    
    // Get all dates from these months
    const dateSet = new Set()
    monthsInRange.forEach(month => {
      const dates = getDatesForMonth(month)
      dates.forEach(date => dateSet.add(date))
    })
    
    return Array.from(dateSet).sort()
  }

  // Month-wise series (click point -> drilldown for that month with ALL destinations)
  const monthSeries = useMemo(() => {
    return stores.map((store) => {
      const storeMap = monthQty[store] || {}

      const data = months.map((m) => {
        const y = storeMap[m] ? storeMap[m] : 0
        return {
          name: m,
          y: y,
          drilldown: true,
          month: m, // Store month for reference
          store: store, // Store store name for reference
        }
      })

      return {
        type: "line",
        name: store,
        data: data,
        marker: { enabled: true },
      }
    })
  }, [stores, months, monthQty])

  // Day-wise series for ALL destinations (used in Day Wise mode)
  const dayAllSeries = useMemo(() => {
    return stores.map((store) => {
      const storeMap = dayAllQty[store] || {}
      const data = filteredDates.map((d) => (storeMap[d] ? storeMap[d] : 0))
      return {
        type: "line",
        name: store,
        data: data,
        marker: { enabled: true },
      }
    })
  }, [stores, filteredDates, dayAllQty])

  // Day-wise series for a specific month (for drilldown)
  const getDaySeriesForMonth = (month) => {
    const monthDates = getDatesForMonth(month)
    
    return stores.map((store) => {
      const storeMap = dayAllQty[store] || {}
      const data = monthDates.map((d) => (storeMap[d] ? storeMap[d] : 0))
      return {
        type: "line",
        name: store,
        data: data,
        marker: { enabled: true },
      }
    })
  }

  const chartOptions = useMemo(() => {
    const isMonth = viewMode === "month"

    // DAY WISE (ALL) view
    if (!isMonth) {
      return {
        chart: { zoomType: "x", height: 500 },
        title: {
          text: dayRange
            ? `Sales Trends (Day Wise - ${dayRange.from} to ${dayRange.to})`
            : clickedMonth
            ? `Sales Trends (Day Wise - ${clickedMonth})`
            : "Sales Trends (Day Wise - All Destinations)",
        },
        xAxis: { 
          categories: clickedMonth ? getDatesForMonth(clickedMonth) : filteredDates, 
          labels: { rotation: -45 } 
        },
        yAxis: { title: { text: "Sale Quantity" } },
        tooltip: {
          shared: false,
          formatter: function () {
            return (
              "<b>" +
              this.x +
              "</b><br/>" +
              this.series.name +
              ": <b>" +
              this.y +
              "</b>"
            )
          },
        },
        credits: { enabled: false },
        series: clickedMonth ? getDaySeriesForMonth(clickedMonth) : dayAllSeries,
      }
    }

    // MONTH WISE (with drilldown click + zoom => go to day wise)
    return {
      chart: {
        zoomType: "x",
        height: 500,
         resetZoomButton: {
    position: { align: "right", x: -10, y: 52 } // move reset-zoom down
  },
        events: {
          // ZOOM on month-wise => switch to day-wise for that month range
          selection: function (e) {
            if (!e || !e.xAxis || !e.xAxis[0]) return true

            const minIdx = Math.max(0, Math.floor(e.xAxis[0].min))
            const maxIdx = Math.min(months.length - 1, Math.ceil(e.xAxis[0].max))

            const fromMonth = months[minIdx]
            const toMonth = months[maxIdx]

            const from = monthStart(fromMonth)
            const to = monthEnd(toMonth)

            // Get dates for the entire month range
            const rangeDates = getDatesForMonthRange(fromMonth, toMonth)
            
            // Set state to show day view with all destinations for this range
            setDayRange({ from: from, to: to })
            setClickedMonth(null) // Clear clicked month
            setViewMode("day")

            return false // stop default zoom
          },

          // CLICK a month point => drilldown day-wise for ALL destinations in that month
          drilldown: function (e) {
            if (!e || !e.point || !e.point.options) return

            const chart = this
            const month = e.point.options.month || e.point.name
            
            // Store the clicked month for reference
            setClickedMonth(month)
            
            // Switch to day view with all destinations for this month
            setDayRange(null) // Clear any range
            setViewMode("day")

            // No need to add series here - the component will re-render with day view
          },

          drillup: function () {
            // This handles drillup from day view back to month view
            const chart = this
            for (let i = 0; i < chart.series.length; i++) {
              chart.series[i].setVisible(true, false)
            }
            chart.redraw()
          },
        },
      },

      title: { text: "Sales Trends (Month Wise)" },
      xAxis: { 
        type: "category", 
        categories: months,
        labels: { rotation: -45 } 
      },
      yAxis: { title: { text: "Sale Quantity" } },
      tooltip: {
        shared: false,
        formatter: function () {
          return (
            "<b>" +
            this.key +
            "</b><br/>" +
            this.series.name +
            ": <b>" +
            this.y +
            "</b>"
          )
        },
      },
      drilldown: {
        series: [],
        drillUpButton: { 
          position: { align: "right", x: -10, y: 10 },
          theme: {
            fill: 'white',
            stroke: 'gray',
            r: 2,
            states: {
              hover: {
                fill: '#e6e6e6'
              }
            }
          }
        },
      },
      credits: { enabled: false },
      series: monthSeries,
    }
  }, [viewMode, months, monthSeries, dayAllSeries, filteredDates, dayRange, clickedMonth, stores, dayAllQty, getDatesForMonth, getDaySeriesForMonth, getDatesForMonthRange])

  // Handle drillup button manually
  const handleDrillUp = () => {
    setViewMode("month")
    setClickedMonth(null)
    setDayRange(null)
  }

return (
  <div style={{ padding: 20 }}>
    <div style={{ position: "relative" }}>
      <HighchartsReact key={viewMode} highcharts={Highcharts} options={chartOptions} />

      {(viewMode === "day" && (dayRange || clickedMonth)) && (
        <button
          onClick={handleDrillUp}
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 9999,
            padding: "8px 14px",
            borderRadius: 8,
            border: "1px solid #ccc",
            background: "#f0f0f0",
            cursor: "pointer",
          }}
        >
          ← Back to Month View
        </button>
      )}
    </div>
  </div>
)

}

export default HistoricChartDrilldown